import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import type { EmailQueueDto } from '../../types/admin';

interface EmailQueueDetailDialogProps {
  emailId: number;
  open: boolean;
  onClose: () => void;
  onRetry: () => void;
}

/**
 * Email Queue Detail Dialog component
 * Shows full email details including body, error history, and metadata
 */
export default function EmailQueueDetailDialog({
  emailId,
  open,
  onClose,
  onRetry
}: EmailQueueDetailDialogProps) {
  const { t } = useTranslation('admin');
  const { token } = useAuth();
  const [email, setEmail] = useState<EmailQueueDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && emailId) {
      loadEmail();
    }
  }, [open, emailId]);

  const loadEmail = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getEmailQueueById(emailId, token);
      setEmail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'default';
      case 'retrying':
        return 'warning';
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('emailQueue.detail.title')}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {email && !loading && (
          <Box>
            {/* Basic Info */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                {t('emailQueue.detail.basicInfo')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {t('emailQueue.columns.id')}
                  </Typography>
                  <Typography variant="body1">{email.id}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {t('emailQueue.columns.status')}
                  </Typography>
                  <Box mt={0.5}>
                    <Chip label={email.status} size="small" color={getStatusColor(email.status)} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {t('emailQueue.columns.type')}
                  </Typography>
                  <Box mt={0.5}>
                    <Chip label={email.emailType} size="small" variant="outlined" />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {t('emailQueue.columns.retries')}
                  </Typography>
                  <Typography variant="body1">
                    {email.retryCount} / {email.maxRetryAttempts}
                  </Typography>
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  {t('emailQueue.columns.recipient')}
                </Typography>
                <Typography variant="body1">{email.recipientEmail}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  {t('emailQueue.columns.subject')}
                </Typography>
                <Typography variant="body1">{email.subject}</Typography>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {t('emailQueue.columns.created')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(email.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                {email.lastAttemptAt && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {t('emailQueue.detail.lastAttempt')}
                    </Typography>
                    <Typography variant="body2">
                      {new Date(email.lastAttemptAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {email.nextRetryAfter && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {t('emailQueue.detail.nextRetry')}
                    </Typography>
                    <Typography variant="body2">
                      {new Date(email.nextRetryAfter).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {email.deliveredAt && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {t('emailQueue.detail.delivered')}
                    </Typography>
                    <Typography variant="body2">
                      {new Date(email.deliveredAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Email Body */}
            <Box mb={3}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                {t('emailQueue.detail.emailBody')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <div dangerouslySetInnerHTML={{ __html: email.body }} />
              </Paper>
            </Box>

            {/* Error History */}
            {email.errorHistory && email.errorHistory.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('emailQueue.detail.errorHistory')}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('emailQueue.detail.attemptNumber')}</TableCell>
                        <TableCell>{t('emailQueue.detail.attemptedAt')}</TableCell>
                        <TableCell>{t('emailQueue.detail.errorMessage')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {email.errorHistory.map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>{error.attemptNumber}</TableCell>
                          <TableCell>{new Date(error.attemptedAt).toLocaleString()}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              {error.errorMessage}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}

            {/* Metadata */}
            {email.metadata && Object.keys(email.metadata).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('emailQueue.detail.metadata')}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(email.metadata, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {email && email.status.toLowerCase() !== 'delivered' && (
          <Button onClick={onRetry} color="primary">
            {t('emailQueue.actions.retry')}
          </Button>
        )}
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
